import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import { TableSkeleton } from "../components/TableSkeleton";
import { tokens } from "../theme";
import { foyerApi } from "../http/foyer";
import type {
  MeteredScreenReport,
  MeteredUsageReport,
} from "@shared/meteredUsage";
import MeteredMonthlyView from "../components/MeteredMonthlyView";

/**
 * Fleet-wide metered-usage pacing dashboard (2026-09-14).
 *
 * A metered premise's ad-rotation contract requires its signage screen(s) on
 * for its own monthly target (N hours, or a share of its open hours). Some sites are good at it, some aren't, and by the time anyone
 * notices at month-end it's too late to increase the ad rotation to make up
 * the shortfall. The "This month" tab is the early warning: every metered
 * premise, coloured by whether it can STILL plausibly reach its target even
 * running its screen for every remaining open hour this month. The "Monthly
 * KPI" tab is the scorecard (`MeteredMonthlyView`).
 *
 *   RED    - can't reach the target even at 100% of the open hours left.
 *   YELLOW - reachable, but only by running close to all of what's left -
 *            the danger zone: no real slack remains.
 *   GREEN  - comfortable room at a normal pace.
 *
 * See `@shared/meteredUsage` for the exact math and `meteredUsageReport.ts`
 * for the server-side roll-up this reads.
 */

const STATUS_LABEL: Record<string, string> = {
  red: "At risk",
  yellow: "Danger zone",
  green: "On track",
};

function statusColors(status: string): { fg: string; bg: string } {
  if (status === "red") return { fg: tokens.error, bg: tokens.errorTint };
  if (status === "yellow")
    return { fg: tokens.warning, bg: tokens.warningTint };
  return { fg: tokens.success, bg: tokens.successTint };
}

function StatusChip({ status }: { status: string }) {
  const c = statusColors(status);
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[status] ?? status}
      sx={{
        bgcolor: c.bg,
        color: c.fg,
        fontWeight: 600,
        border: `1px solid ${c.fg}33`,
      }}
    />
  );
}

function hours(n: number): string {
  return `${n.toLocaleString("sv-SE", { maximumFractionDigits: 1 })}h`;
}

/** The screen furthest from its own target (lowest best-case/target) -
 *  what the premise's colour is really about. Targets differ per screen when
 *  one was paired mid-month, so compare ratios, not raw hours. */
function weakestScreen(
  screens: MeteredScreenReport[]
): MeteredScreenReport | undefined {
  const ratio = (s: MeteredScreenReport) =>
    s.targetHours > 0 ? s.ceilingHours / s.targetHours : Infinity;
  return [...screens].sort((a, b) => ratio(a) - ratio(b))[0];
}

export default function MeteredUsagePage() {
  const [tab, setTab] = useState<"pacing" | "monthly">("pacing");
  const [report, setReport] = useState<MeteredUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await foyerApi.fleetMeteredUsage();
        if (!cancelled) {
          setReport(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      }
    }
    load();
    const t = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);

  const premises = report?.premises ?? [];
  const counts = {
    red: premises.filter((p) => p.status === "red").length,
    yellow: premises.filter((p) => p.status === "yellow").length,
    green: premises.filter((p) => p.status === "green").length,
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Fleet health"
        title="Metered usage"
        subtitle="Premises contracted to keep signage on - pacing for this month, and the monthly scorecard."
      />
      <Tabs
        value={tab}
        onChange={(_e, v: "pacing" | "monthly") => setTab(v)}
        sx={{ mb: 3, borderBottom: `1px solid ${tokens.divider}` }}
      >
        <Tab
          value="pacing"
          label="This month"
          data-testid="metered-tab-pacing"
        />
        <Tab
          value="monthly"
          label="Monthly KPI"
          data-testid="metered-tab-monthly"
        />
      </Tabs>
      {tab === "monthly" ? (
        <MeteredMonthlyView />
      ) : (
        <>
          {error && (
            <Typography sx={{ color: tokens.error, mb: 2 }}>{error}</Typography>
          )}
          <Stack
            direction="row"
            sx={{ gap: 3, flexWrap: "wrap", mb: 3, alignItems: "stretch" }}
          >
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label="At risk"
                value={String(counts.red)}
                sub="Can't reach the target even at best case"
                icon={<ErrorOutlineIcon />}
                tint={tokens.errorTint}
                accent={tokens.error}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label="Danger zone"
                value={String(counts.yellow)}
                sub="Needs almost every open hour left"
                icon={<WarningAmberOutlinedIcon />}
                tint={tokens.warningTint}
                accent={tokens.warning}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
              <KpiCard
                label="On track"
                value={String(counts.green)}
                sub="Comfortable room at a normal pace"
                icon={<CheckCircleOutlineIcon />}
                tint={tokens.successTint}
                accent={tokens.success}
              />
            </Box>
          </Stack>

          {report === null ? (
            <TableSkeleton rows={5} />
          ) : premises.length === 0 ? (
            <Box
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: "12px",
                p: 5,
                textAlign: "center",
                bgcolor: tokens.surface,
              }}
              data-testid="metered-usage-empty"
            >
              <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
                No metered premises yet
              </Typography>
              <Typography
                sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}
              >
                Turn on &ldquo;Metered usage&rdquo; on a premise&apos;s Overview
                tab once it has a signage screen.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: "12px",
              }}
            >
              <Table size="small" data-testid="metered-usage-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Premise</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Hours so far</TableCell>
                    <TableCell align="right">Open hours left</TableCell>
                    <TableCell align="right">Best-case total</TableCell>
                    <TableCell align="right">Screens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {premises.map((p) => {
                    const noData = p.screens.every((s) => !s.hasData);
                    const outsideHours =
                      p.screens.reduce(
                        (s, x) => s + (x.playedHoursMTD - x.onHoursMTD),
                        0
                      ) / Math.max(1, p.screens.length);
                    const darkCount = p.screens.filter(
                      (s) => s.status !== "green"
                    ).length;
                    return (
                      <TableRow key={p.premiseId} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600 }}>
                            {p.premiseName}
                          </Typography>
                          {p.timeZoneInvalid && (
                            <Tooltip
                              title={`Timezone "${p.timeZoneInvalid}" isn't a real zone - computed in UTC instead. Fix it on the premise's Location card.`}
                            >
                              <Typography
                                sx={{ fontSize: 11, color: tokens.warning }}
                              >
                                Timezone unset/invalid
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={p.status} />
                          {noData && (
                            <Tooltip title="No screen at this premise has reported playback yet this month - these hours are an unconfirmed zero, not a measured one.">
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: tokens.inkMuted,
                                  mt: 0.25,
                                }}
                              >
                                No data yet
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {hours(
                            p.screens.reduce((s, x) => s + x.onHoursMTD, 0) /
                              Math.max(1, p.screens.length)
                          )}
                          {outsideHours >= 0.1 && (
                            <Tooltip title="Airtime while the premise is closed doesn't count toward the target - only play inside opening hours does. Check the premise's opening hours if this screen is meant to run then.">
                              <Typography
                                sx={{ fontSize: 11, color: tokens.inkMuted }}
                              >
                                +{hours(outsideHours)} outside opening hours
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {hours(
                            Math.min(
                              ...p.screens.map((s) => s.remainingOpenHours)
                            )
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const w = weakestScreen(p.screens);
                            return w ? (
                              <>
                                {hours(w.ceilingHours)}
                                {" / "}
                                {hours(w.targetHours)}
                              </>
                            ) : (
                              "-"
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {darkCount > 0
                            ? `${darkCount}/${p.screens.length} at risk`
                            : `${p.screens.length} healthy`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </PageContainer>
  );
}
